import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

function LoginErrorFallback({ reset }: { error: Error; reset: () => void }) {
  return (
    <div
      className="flex min-h-screen items-center justify-center px-6"
      style={{ backgroundColor: "var(--caileve-bg)" }}
    >
      <div className="max-w-sm text-center">
        <h1
          className="text-2xl"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-lilac)" }}
        >
          Something went wrong.
        </h1>
        <p
          className="mt-3 text-base"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-ink)" }}
        >
          Tap to reload.
        </p>
        <button
          type="button"
          onClick={() => {
            reset();
            if (typeof window !== "undefined") window.location.reload();
          }}
          className="mt-6 px-6 py-2.5 text-xs uppercase tracking-[0.25em] text-[var(--caileve-bg)]"
          style={{ backgroundColor: "var(--caileve-lilac)" }}
        >
          Reload
        </button>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/login")({
  component: LoginPage,
  errorComponent: LoginErrorFallback,
});

function LoginPage() {
  const { user, signIn, signUp, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const signingUp = useRef(false);

  useEffect(() => {
    if (!loading && user && !signingUp.current) navigate({ to: "/" });
  }, [user, loading, navigate]);

  // Handle post-confirmation / OAuth redirect signals from URL.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    if (params.get("confirmed") === "1") {
      toast.success("Your email has been confirmed. Please sign in.");
      params.delete("confirmed");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
    const errDesc = hash.get("error_description") || params.get("error_description");
    if (errDesc) {
      const lower = errDesc.toLowerCase();
      if (
        lower.includes("already") ||
        lower.includes("identity") ||
        lower.includes("exists")
      ) {
        toast.error(
          "An account with this email already exists. Please sign in with your original method.",
        );
      } else {
        toast.error("Authentication error. Please try again.");
      }
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handle = async (mode: "in" | "up") => {
    if (!email || !password) {
      toast.error("Email and password required");
      return;
    }
    if (mode === "up") signingUp.current = true;
    setBusy(true);
    const { error } = mode === "in" ? await signIn(email, password) : await signUp(email, password);
    setBusy(false);
    if (error) {
      signingUp.current = false;
      toast.error(error);
    } else if (mode === "up") {
      navigate({ to: "/confirm-email", search: { email } });
    }
  };

  async function sendReset() {
    const target = forgotEmail.trim() || email.trim();
    if (!target) {
      toast.error("Enter your email first.");
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(target, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setForgotOpen(false);
    toast.success("Check your email for a reset link.");
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <h1 className="font-serif-display text-5xl text-foreground">Cailève</h1>
          <p className="text-muted-foreground mt-2 tracking-widest text-xs uppercase">
            Your curated wardrobe
          </p>
        </div>

        <div className="bg-card border border-border p-8 shadow-sm">
          <h2
            className="text-center mb-6"
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              color: "var(--caileve-ink)",
              fontSize: "2.25rem",
              lineHeight: 1.1,
            }}
          >
            Welcome back.
          </h2>
          <Button
            type="button"
            variant="outline"
            className="w-full mb-4"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              const { lovable } = await import("@/integrations/lovable");
              const result = await lovable.auth.signInWithOAuth("google", {
                redirect_uri: window.location.origin,
              });
              if (result.error) {
                setBusy(false);
                toast.error("Could not sign in with Google");
              }
            }}
          >
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </Button>
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center">
              <span className="bg-card px-3 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">or</span>
            </div>
          </div>
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>

            {(["signin", "signup"] as const).map((tab) => (
              <TabsContent key={tab} value={tab} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor={`${tab}-email`}>Email</Label>
                  <Input
                    id={`${tab}-email`}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`${tab}-password`}>Password</Label>
                  <Input
                    id={`${tab}-password`}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <Button
                  className="w-full"
                  disabled={busy}
                  onClick={() => handle(tab === "signin" ? "in" : "up")}
                >
                  {tab === "signin" ? "Sign In" : "Create Account"}
                </Button>
                {tab === "signin" && (
                  <div className="pt-1 text-center">
                    {forgotOpen ? (
                      <div className="space-y-2">
                        <Input
                          type="email"
                          placeholder="Your email"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                        />
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            className="flex-1"
                            onClick={() => setForgotOpen(false)}
                            disabled={busy}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            className="flex-1"
                            disabled={busy}
                            onClick={sendReset}
                            style={{ backgroundColor: "var(--caileve-lilac)", color: "var(--caileve-bg)" }}
                          >
                            Send reset link
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setForgotEmail(email);
                          setForgotOpen(true);
                        }}
                        className="text-xs hover:underline"
                        style={{ color: "var(--caileve-lilac-text)" }}
                      >
                        Forgot your password?
                      </button>
                    )}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>

        <p className="text-center text-xs mt-6 leading-relaxed text-muted-foreground px-4">
          By continuing you agree to our{" "}
          <Link to="/terms" style={{ color: "var(--caileve-lilac-text)" }} className="hover:underline">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link to="/privacy" style={{ color: "var(--caileve-lilac-text)" }} className="hover:underline">
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </div>
  );
}